import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { User, Mail, Pencil, Check, X, Lock, LogOut, Flame, Trophy, Target, CheckCircle2, Users, Loader2, ShieldCheck, Crown, CalendarClock, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LevelBadge from "@/components/LevelBadge";
import { Avatar } from "@/components/Avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Profile() {
  const { user, logout, setUser, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [editName, setEditName] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { setName(user?.name || ""); }, [user?.name]);

  const saveName = async () => {
    if (!name.trim()) return;
    setSavingName(true);
    try {
      const { data } = await api.put("/me/profile", { name: name.trim() });
      setUser(data);
      setEditName(false);
      toast.success("Name updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingName(false); }
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large — keep it under 5MB"); return; }
    setUploadingAvatar(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/me/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      toast.success("Profile photo updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploadingAvatar(false); }
  };

  const removeAvatar = async () => {
    setUploadingAvatar(true);
    try {
      const { data } = await api.delete("/me/avatar");
      setUser(data);
      toast.success("Photo removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploadingAvatar(false); }
  };

  const initial = (user?.name || user?.email || "U")[0].toUpperCase();

  const statCards = [
    { icon: <Trophy className="w-5 h-5 text-amber-400" />, label: "Level", value: user?.level ?? "—" },
    { icon: <Target className="w-5 h-5 text-blue-400" />, label: "Total XP", value: user?.xp ?? 0 },
    { icon: <Flame className="w-5 h-5 text-orange-500" />, label: "Best Streak", value: stats?.best_streak ?? 0 },
    { icon: <CheckCircle2 className="w-5 h-5 text-emerald-400" />, label: "Completions", value: stats?.completions ?? 0 },
    { icon: <ShieldCheck className="w-5 h-5 text-purple-400" />, label: "Achievements", value: `${stats?.unlocked_achievements ?? 0}/${stats?.total_achievements ?? 10}` },
    { icon: <Users className="w-5 h-5 text-teal-400" />, label: "Squad", value: stats?.friends ?? 0 },
  ];

  return (
    <div className="space-y-8 pb-24 md:pb-8 max-w-3xl" data-testid="profile-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-2">
          <User className="w-3.5 h-3.5" /> Account
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Your Profile</h1>
        <p className="text-white mt-2">Manage your details, security and see your progress.</p>
      </div>

      {/* Identity */}
      <div className="p-5 sm:p-6 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            <Avatar
              userId={user?.id}
              name={user?.name}
              email={user?.email}
              hasAvatar={user?.has_avatar}
              version={user?.avatar_version}
              className="w-16 h-16 glow-emerald"
              textClassName="text-2xl"
              rounded="rounded-2xl"
              testid="profile-avatar"
            />
            <button
              data-testid="profile-avatar-upload"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingAvatar}
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-emerald-500 text-black flex items-center justify-center border-2 border-[#12141C] hover:bg-emerald-400 transition-colors disabled:opacity-60"
              aria-label="Change photo"
            >
              {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            </button>
            <input ref={fileRef} data-testid="profile-avatar-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onPickAvatar} />
          </div>
          <div className="flex-1 min-w-0">
            {editName ? (
              <div className="flex gap-2">
                <input
                  data-testid="profile-name-input"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveName()}
                  className="flex-1 h-10 px-3 rounded-lg bg-[#0D0F17] border border-[#242417] outline-none focus:border-emerald-400 text-white"
                />
                <button data-testid="profile-name-save" onClick={saveName} disabled={savingName} className="w-10 h-10 rounded-lg bg-emerald-500 text-black flex items-center justify-center">
                  {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-5 h-5" />}
                </button>
                <button onClick={() => { setEditName(false); setName(user?.name || ""); }} className="w-10 h-10 rounded-lg border border-[#33331f] text-white flex items-center justify-center">
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="font-display text-xl font-bold truncate">{user?.name}</h2>
                <button data-testid="profile-name-edit" onClick={() => setEditName(true)} className="text-white hover:text-emerald-400">
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-white mt-1">
              <Mail className="w-4 h-4 text-emerald-400" />
              <span className="truncate">{user?.email}</span>
            </div>
            {user?.has_avatar && (
              <button
                data-testid="profile-avatar-remove"
                onClick={removeAvatar}
                disabled={uploadingAvatar}
                className="mt-2 text-xs text-white/60 hover:text-red-400 transition-colors disabled:opacity-60"
              >
                Remove photo
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Level */}
      {user && <LevelBadge level={user.level} progress={user.level_progress} need={user.level_need} xp={user.xp} />}

      {/* Focus */}
      <FocusEditor user={user} setUser={setUser} refreshUser={refreshUser} />

      {/* Billing */}
      <BillingSection isAdmin={user?.role === "admin"} refreshUser={refreshUser} />

      {/* Stats */}
      <div>
        <h3 className="font-display text-lg font-semibold mb-4">Your progress</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {statCards.map((s, i) => (
            <div key={i} data-testid={`profile-stat-${i}`} className="p-4 rounded-2xl border border-[#242417] bg-[#12141C]">
              <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center mb-3">{s.icon}</div>
              <div className="font-mono-stat text-2xl font-bold">{s.value}</div>
              <div className="text-xs text-white mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Security */}
      <ChangePassword />

      {/* Sign out */}
      <button
        data-testid="profile-signout-button"
        onClick={logout}
        className="w-full h-12 rounded-xl border border-red-500/30 text-red-400 font-semibold flex items-center justify-center gap-2 hover:bg-red-500/10 transition-colors"
      >
        <LogOut className="w-5 h-5" /> Sign Out
      </button>
    </div>
  );
}

function FocusEditor({ user, setUser }) {
  const [editing, setEditing] = useState(false);
  const [focus, setFocus] = useState(user?.focus || "");
  useEffect(() => { setFocus(user?.focus || ""); }, [user?.focus]);

  const save = async () => {
    try {
      await api.put("/me/focus", { focus });
      setUser((u) => ({ ...u, focus }));
      setEditing(false);
      toast.success("Focus updated");
    } catch (e) { toast.error("Couldn't save"); }
  };

  return (
    <div className="p-5 rounded-2xl border border-[#242417] bg-[#12141C]">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-5 h-5 text-emerald-400" />
        <h3 className="font-display text-lg font-semibold">My focus this month</h3>
      </div>
      {editing ? (
        <div className="flex gap-2">
          <input
            data-testid="profile-focus-input"
            autoFocus
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            placeholder="e.g. Move more, sleep better"
            className="flex-1 h-10 px-3 rounded-lg bg-[#0D0F17] border border-[#242417] outline-none focus:border-emerald-400 text-white"
          />
          <button data-testid="profile-focus-save" onClick={save} className="h-10 px-4 rounded-lg bg-emerald-500 text-black font-semibold">Save</button>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} className="text-left w-full group flex items-center gap-2">
          <span className="text-white">{user?.focus || "Set your focus for the month"}</span>
          <Pencil className="w-3.5 h-3.5 text-white group-hover:text-emerald-400" />
        </button>
      )}
    </div>
  );
}

function BillingSection({ isAdmin, refreshUser }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = () => api.get("/billing").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const CUR = { gbp: "£", usd: "$", eur: "€" };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription will end at your renewal date");
      await load();
      refreshUser();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); setConfirmCancel(false); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await api.post("/billing/resume");
      toast.success("Subscription resumed");
      await load();
      refreshUser();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (isAdmin) {
    return (
      <div className="p-5 rounded-2xl border border-[#242417] bg-[#12141C]" data-testid="billing-section">
        <div className="flex items-center gap-2 mb-2">
          <Crown className="w-5 h-5 text-amber-400" />
          <h3 className="font-display text-lg font-semibold">Billing</h3>
        </div>
        <p className="text-white text-sm">You have admin access — no subscription needed.</p>
      </div>
    );
  }

  const symbol = info ? (CUR[info.currency] || "£") : "£";
  const price = info?.amount != null ? (info.amount / 100).toFixed(2) : "9.99";
  const renewIso = info?.current_period_end || info?.premium_until;

  return (
    <div className="p-5 sm:p-6 rounded-2xl border border-[#242417] bg-[#12141C]" data-testid="billing-section">
      <div className="flex items-center gap-2 mb-4">
        <CalendarClock className="w-5 h-5 text-emerald-400" />
        <h3 className="font-display text-lg font-semibold">Billing & subscription</h3>
      </div>

      {!info ? (
        <div className="text-white text-sm">Loading…</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className={`text-xs px-3 py-1 rounded-full border font-semibold ${info.is_premium ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-white/5 text-white border-[#33331f]"}`}>
              {info.is_premium ? "Momentum Pro · Active" : "Free"}
            </span>
            {info.cancel_at_period_end && (
              <span className="text-xs px-3 py-1 rounded-full border bg-amber-500/10 text-amber-400 border-amber-500/30 font-semibold" data-testid="billing-cancel-badge">
                Cancels at period end
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div className="p-4 rounded-xl bg-white/[0.02] border border-[#242417]">
              <div className="text-xs uppercase tracking-wider text-white mb-1">Plan</div>
              <div className="font-semibold">{symbol}{price} / {info.interval || "year"}</div>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02] border border-[#242417]">
              <div className="text-xs uppercase tracking-wider text-white mb-1">
                {info.cancel_at_period_end ? "Access until" : "Renews on"}
              </div>
              <div className="font-semibold" data-testid="billing-renewal-date">{fmtDate(renewIso)}</div>
            </div>
          </div>

          {info.has_subscription ? (
            info.cancel_at_period_end ? (
              <button data-testid="billing-resume-button" onClick={resume} disabled={busy} className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Resume subscription
              </button>
            ) : (
              <button data-testid="billing-cancel-button" onClick={() => setConfirmCancel(true)} disabled={busy} className="w-full h-11 rounded-xl border border-red-500/30 text-red-400 font-semibold hover:bg-red-500/10 transition-colors">
                Cancel subscription
              </button>
            )
          ) : (
            <p className="text-white text-sm">No active subscription on file.</p>
          )}
        </>
      )}

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent className="bg-[#12141C] border-[#242417] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription className="text-white">
              You'll keep full access until {fmtDate(renewIso)}, then your account returns to Free. You can resume anytime before then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#33331f] text-white hover:bg-white/5">Keep plan</AlertDialogCancel>
            <AlertDialogAction data-testid="billing-confirm-cancel" onClick={cancel} className="bg-red-500 hover:bg-red-600 text-white">Cancel subscription</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 6) return setError("New password must be at least 6 characters");
    if (form.new_password !== form.confirm) return setError("New passwords don't match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="p-5 sm:p-6 rounded-2xl border border-[#242417] bg-[#12141C]" data-testid="change-password-form">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-5 h-5 text-emerald-400" />
        <h3 className="font-display text-lg font-semibold">Change password</h3>
      </div>
      <div className="space-y-3">
        {[
          ["current_password", "Current password", "current-password-input"],
          ["new_password", "New password", "new-password-input"],
          ["confirm", "Confirm new password", "confirm-password-input"],
        ].map(([key, label, tid]) => (
          <div key={key}>
            <label className="block text-xs uppercase tracking-wider text-white mb-2">{label}</label>
            <input
              data-testid={tid}
              type="password"
              required
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
              placeholder="••••••••"
              className="w-full h-11 px-4 rounded-xl bg-[#0D0F17] border border-[#242417] outline-none focus:border-emerald-400 text-white"
            />
          </div>
        ))}
        {error && <div data-testid="change-password-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</div>}
        <button data-testid="change-password-submit" type="submit" disabled={saving} className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Update Password
        </button>
      </div>
    </form>
  );
}
